/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Money } from "../common/currency.pb";
import type { ClientScenario } from "./api.pb";

export enum LendingProduct {
  BORROW = "BORROW",
  BNPL = "BNPL",
  CF_BNPL = "CF_BNPL",
  TAX_REFUND_ADVANCE = "TAX_REFUND_ADVANCE",
  RETROACTIVE_FINANCING = "RETROACTIVE_FINANCING",
  SINGLE_USE_PAYMENT = "SINGLE_USE_PAYMENT",
}

export interface CreditLine {
  /** The token identifying this line of credit. */
  token?: string;
  /** The maximum amount that can be borrowed from this line of credit. */
  credit_limit?: Money;
  /** The current amount available to be borrowed from this line of credit. */
  available_amount?: Money;
  /** The amount owed on this line of credit. */
  outstanding_amount?: Money;
  /** The initial setup fee percentage. */
  setup_fee_bps?: number;
  /** The ATM picker amounts. */
  quick_amounts?: Money[];
  /** The minimum amount of a loan. */
  minimum_loan_amount?: Money;
  /**
   * The text components for the first time borrow screen.
   *
   * @deprecated
   */
  first_time_borrow_data?: CreditLineFirstTimeBorrowData;
  /**
   * Deprecated We now gate access to borrow using functionality in the Lending Config.
   *  Information to unlock Borrow. Null if already unlocked.
   *
   * @deprecated
   */
  unlock_borrow_data?: CreditLineUnlockBorrowData;
  /** The title displayed on the Borrow button on the banking tab: "Borrow up to $200". */
  instrument_display_name?: string;
  /** An icon to be displayed on the borrow instrument. Use null for no status. */
  credit_line_status_icon?: CreditLineCreditLineStatusIcon;
  /** Indicates whether to skip the atm picker when initiating a loan. */
  skip_loan_amount_selection?: boolean;
  /** Shows underneath the instrument_display_name on the instrument */
  instrument_subtitle?: string;
  /** The lending product this credit line belongs to. */
  lending_product?: LendingProduct;
  /** See CreditLineStatusData message. */
  credit_line_status_data?: CreditLineCreditLineStatusData;
  /** See CreditLineLimitData message. */
  credit_line_limit_data?: CreditLineCreditLineLimitData;
  /** An optional credit line alert. */
  alert?: CreditLineAlert;
  /** Localizable field for instrument_display_name */
  localizable_instrument_display_name?: LocalizableString;
  /** Localizable field for instrument_subtitle */
  localizable_instrument_subtitle?: LocalizableString;
}

/** An icon used to show the user global information about their creidit line. */
export enum CreditLineCreditLineStatusIcon {
  EXCEPTION = "EXCEPTION",
}

export interface CreditLineAlert {
  /** An alert message to display to the user when they view their credit line. This field is mandatory. */
  message?: string;
  /** The title of the dialog dismiss button. This field is mandatory. */
  dismiss_button_title?: string;
  /** A title for the message displayed to the user. This field is mandatory. */
  title?: string;
  /** A non optional DisplayTime. */
  displayTime?: CreditLineAlertDisplayTime;
}

/** Describes when the alert will be displayed to the user. */
export enum CreditLineAlertDisplayTime {
  /** BORROW_ENTRY - To be shown upon entry into the Borrow screen. */
  BORROW_ENTRY = "BORROW_ENTRY",
}

/**
 * Deprecated We now gate access to borrow using functionality in the Lending Config.
 * The UnlockBorrowData contains information about unlocking Borrow
 * for users who are not meeting all of the criteria to unlock.
 *
 * @deprecated
 */
export interface CreditLineUnlockBorrowData {
  /** The title to be displayed on the unlock pill. */
  unlock_button_title?: string;
  /** The ClientScenario to initiate when a customer taps the Borrow instrument or unlock button. */
  client_scenario?: ClientScenario;
}

/**
 * The CreditLineLimitData contains information to display to the user on the
 * credit limit screen.
 */
export interface CreditLineCreditLineLimitData {
  /** Title to display in the fee section. */
  title?: string;
  /** Subtitle, if any, to display below the title. */
  subtitle?: string;
  /** Additional info, if any, to display above the “Learn More” button. */
  additionalInfoText?: string;
  /** The bullet text to display. */
  bullets?: CreditLineCreditLineLimitDataBullet[];
  /** The url to route the customer to when they tap to learn about their credit limit. */
  url?: string;
  /** Localizable field for title */
  localizable_title?: LocalizableString;
  /** Localizable field for subtitle */
  localizable_subtitle?: LocalizableString;
  /** Localizable field for additionalInfoText */
  localizable_additionalInfoText?: LocalizableString;
}

export interface CreditLineCreditLineLimitDataBullet {
  text?: string;
  icon?: CreditLineCreditLineLimitDataBulletIcon;
  /** Localizable field for CreditLineLimitData.Bullet.text */
  localizable_text?: LocalizableString;
}

export enum CreditLineCreditLineLimitDataBulletIcon {
  TIME = "TIME",
  DEPOSIT = "DEPOSIT",
  BANK = "BANK",
}

/**
 * The CreditLineStatusData contains server-generated text and information to display
 * relevent loan information to the user such as credit limit information.
 */
export interface CreditLineCreditLineStatusData {
  /** Icon to display on the left of the fee section. */
  icon?: CreditLineCreditLineStatusDataIcon;
  /** Title to display in the fee section. */
  title?: string;
  /** Subtitle, if any, to display below the title. */
  subtitle?: string;
  /** True if we want to show the red badge on the banking tab. */
  badged?: boolean;
  /** The ClientScenario to initiate when a customer taps the status section. */
  client_scenario?:
    | ClientScenario
    | undefined;
  /** The client route to resolve when a customer taps the status section. */
  client_route?:
    | string
    | undefined;
  /** Localizable field for title */
  localizable_title?: LocalizableString;
  /** Localizable field for subtitle */
  localizable_subtitle?: LocalizableString;
}

export enum CreditLineCreditLineStatusDataIcon {
  CHECKMARK = "CHECKMARK",
  INFO = "INFO",
}

/**
 * The FirstTimeBorrowData contains server-generated text to display to the user the first
 * time they choose to borrow money.
 */
export interface CreditLineFirstTimeBorrowData {
  /** "Borrow up to $200" */
  title?: string;
  /** "Pay it back over time at an industry-low rate of 5%" */
  subtitle?: string;
  /** "Get Started" */
  button_title?: string;
  /**
   * "Receive Funds Instantly"
   * "Pay only a 5% flat fee"
   * "Repay within 4 weeks"
   */
  bullets?: string[];
  /** "Learn More". If null, client will suppress the button. */
  support_button_title?: string;
  /**
   * The support node to navigate to for the "Learn More" button. Required if support_button_title
   * is non-null.
   */
  support_node?: string;
  promo_content?: CreditLineFirstTimeBorrowDataPromoContent;
  /** The title for the notice / disclaimer about opting out. */
  notice_title?: string;
  /**
   * The body of the notice / disclaimer about opting out.  This text may include
   * markdown-style links that the client must parse and linkify.
   */
  notice_body?: string;
  localizable_title?: LocalizableString;
}

/** Contains server-generated content for the My First Borrow/Loan component */
export interface CreditLineFirstTimeBorrowDataPromoContent {
  /** "My First Borrow" */
  title?: string;
  /** "We created a tiny picture book..." */
  subtitle?: string;
  /** "Start Reading" */
  button_title?: string;
  /** The support node to navigate to when clicking the button. Takes priority over button_url. */
  button_support_node?: string;
  /** The URL to navigate to when clicking the button. Ignored if button_support_node is set. */
  button_url?: string;
}

export interface Loan {
  /** The token identifying this loan. */
  token?: string;
  /** The token identifying the line of credit for this loan. */
  credit_line_token?: string;
  /** The datetime, in millis since epoch, when this loan was drawn. */
  borrowed_at?: number;
  /** The amount that was borrowed. This does not include any fees. */
  principal_amount?: Money;
  /** The amount of the fee to setup this loan. */
  setup_fee_amount?: Money;
  /** The amount that is owed on this loan. */
  outstanding_amount?: Money;
  /** The amount of late fees applied to this loan. */
  late_fee_amount?: Money;
  /** The amount of interest applied to this loan. */
  interest_amount?: Money;
  /** The initial setup fee percentage. */
  setup_fee_bps?: number;
  /** The detail display rows for a loan. */
  detail_rows?: LoanDetailRow[];
  /** The state of the loan. */
  state?: LoanState;
  /**
   * The final due date of the loan. This is used to determine if and how long
   *  overdue a loan is.
   */
  due_at?: number;
  /** The lending product this loan belongs to. */
  lending_product?: LendingProduct;
  /** Buy Now, Pay Later data for loan page. */
  bnpl_data?: LoanBnplData;
}

export enum LoanState {
  OPEN = "OPEN",
  COMPLETE = "COMPLETE",
  OVERDUE = "OVERDUE",
  WRITTEN_OFF = "WRITTEN_OFF",
  /** @deprecated */
  LONG_OVERDUE = "LONG_OVERDUE",
}

export interface LoanDetailRow {
  label?: string;
  value?: string;
  /** Localizable field for label */
  localizable_label?: LocalizableString;
  /** Localizable field for value */
  localizable_value?: LocalizableString;
}

/** Buy Now, Pay Later data for loan page. */
export interface LoanBnplData {
  split_payment_description?: string;
  loan_details_description?: string;
  merchant_customer_token?: string;
}

export interface LoanTransaction {
  type?: LoanTransactionType;
  /** The token identifying this loan transaction. */
  token?: string;
  /** The token identifying the credit line for this transaction. */
  credit_line_token?: string;
  /** The token identifying the loan for this transaction. */
  loan_token?: string;
  /**
   * The datetime, in millis since epoch, that this loan transaction appears in the timeline. For
   * scheduled, skipped, or missed payments, this is the date that the payment was scheduled. For
   * completed payments, this is the date it was paid. For overdue payments, this will be null.
   * For drawdowns, this will be the date of the drawdown.  For charges, this will be the date
   * the charge was applied.
   */
  date?: number;
  /** The lending product this transaction belongs to. */
  lending_product?: LendingProduct;
  loan_payment?: LoanTransactionLoanPayment;
  loan_drawdown?: LoanTransactionLoanDrawdown;
  loan_charge?: LoanTransactionLoanCharge;
  /** The entity ID of the object in the activity list corresponding to this transaction. May not exactly match the object's token. */
  activity_token?: string;
  loan_refund?: LoanTransactionLoanRefund;
  loan_adjustment?: LoanTransactionLoanAdjustment;
  /**
   * Information to further describe the transcation state. Shown in different format
   *
   * @deprecated
   */
  transaction_detail_text?: string;
  /** A description to appear inline on payment details timeline. */
  detailDescription?: string;
  /** Localizable field for detailDescription */
  localizable_detailDescription?: LocalizableString;
}

export enum LoanTransactionType {
  PAYMENT = "PAYMENT",
  DRAWDOWN = "DRAWDOWN",
  CHARGE = "CHARGE",
  REFUND = "REFUND",
  ADJUSTMENT = "ADJUSTMENT",
  OVERDUE_PAYMENT_PLAN_PAYMENT = "OVERDUE_PAYMENT_PLAN_PAYMENT",
}

export interface LoanTransactionLoanPayment {
  state?: LoanTransactionLoanPaymentState;
  /**
   * The amount of the payment. This is the amount due for a scheduled, skipped,
   * missed or overdue payment, and the amount paid for a completed payment.
   */
  amount?: Money;
}

export enum LoanTransactionLoanPaymentState {
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
  SKIPPED = "SKIPPED",
  MISSED = "MISSED",
  OVERDUE = "OVERDUE",
  CANCELLED = "CANCELLED",
}

export interface LoanTransactionLoanDrawdown {
  /** The amount of the drawdown including principal and setup fee. */
  amount?: Money;
}

export interface LoanTransactionLoanCharge {
  charge_type?: LoanTransactionLoanChargeChargeType;
  /** The amount of the late fee or interest. */
  amount?: Money;
  /** The amount of the payment that was missed. */
  missed_amount?: Money;
}

export enum LoanTransactionLoanChargeChargeType {
  LATE_FEE = "LATE_FEE",
  INTEREST = "INTEREST",
}

export interface LoanTransactionLoanRefund {
  /** The amount of the refund. */
  amount?: Money;
}

export interface LoanTransactionLoanAdjustment {
  /** The amount of the adjustment. */
  amount?: Money;
}

export interface LendingConfig {
  /** When false, the app should hide everything related to lending. */
  enabled?: boolean;
  /**
   * DEPRECATED: This client scenario will run when the user taps on the Borrow instrument. If enabled is
   * true, the borrow screen will be presented upon completion of the flow. Otherwise, the
   * user will not be able to access the Lending screen.
   */
  instrument_client_scenario?: ClientScenario;
  /**
   * DEPRECATED: This client scenario will run when the client is resolving deep links into Lending. If
   * enabled is  true, the borrow screen will be presented upon completion of the flow.
   * Otherwise, the user will not be able to access the Lending screen.
   */
  deep_link_client_scenario?: ClientScenario;
  /** InstrumentData. Not optional */
  instrument_data?: LendingConfigInstrumentData;
  /**
   * This Routing will run when the client is resolving deep links into Lending. As soon as the
   * deep link is tapped (or resolved), the local LendingConfig will be invalidated, and a new LendingConfig
   * will be immediately fetched. If `enabled` is true and `deep_link_routing` is a client scenario
   * the user will be dropped on the Borrow screen. If `enabled` is false and the above is true, the
   * user will end up on the Banking tab upon completion of the flow. Regardless of whether or not
   * `enabled` is true the user will not be able to access borrow if a Client Route is provided.
   * The client route will be resolved each time the user attempts to tap on a link that takes them
   * to borrow.
   *
   * This field is optional.
   */
  deep_link_routing?: LendingConfigRouting;
  /**
   * This Routing will run when user taps on the Borrow instrument. As soon as the user taps, the
   * local LendingConfig will be invalidated, and a new LendingConfig will be immediately fetched.
   * If `enabled` is true and `instrument_routing` is a client scenario the user will be dropped on
   * the Borrow screen. If `enabled` is false and the above is true, the
   * user will end up on the Banking tab upon completion of the flow. Regardless of whether or not
   * `enabled` is true the user will not be able to access borrow if a Client Route is provided.
   * The client route will be resolved each time the user attempts to tap on the Borrow instrument.
   *
   * This field is optional.
   */
  instrument_routing?: LendingConfigRouting;
  /**
   * Timestamp, in epoch milliseconds, when the client should refresh its local copy of
   * LendingConfig. If the client's current date-time is after this date-time, the client will
   * refresh its LendingConfig.
   *
   * This value is optional: if null the client will never force-refresh its local LendingConfig
   * (but may refresh it if it is invalidated some other way).
   */
  expires_at?: number;
}

export interface LendingConfigRouting {
  client_scenario?: ClientScenario | undefined;
  client_route?: string | undefined;
}

export interface LendingConfigInstrumentData {
  /**
   * An optional property that, if true, will show a red badge on
   * the Borrow instrument on the banking tab. Note: Another way a
   * badge may show is if the user's CreditLine has StatusData
   * populated.
   */
  badged?: boolean;
  /**
   * The title of the Borrow instrument shown on the Banking tab.
   * Not optional.
   */
  instrument_title?: string;
  /**
   * The subtitle of the Borrow instrument shown on the Banking tab.
   * Not optional.
   */
  instrument_subtitle?: string;
}
